/**
 * DigiTrax / HemaTraxCT label message builder (Data Structure v 3.7.3).
 *
 * Pure and dependency-free like the scheduling engine, so the 51-field
 * serialization and its date/barcode formats can be unit-tested. The wire
 * format is a text file: ESC (0x1B), fields 2–50 one per line, EOT (0x04).
 * Fields we have no value for serialize as empty lines — HemaTrax treats
 * blank as "not provided".
 *
 * Transport is NOT here: there is no DigiTrax endpoint yet. `sendToDigiTrax`
 * below is the single seam to replace when Digi-Trax provides one.
 */

/** Everything HemaTraxCT v3.7.3 can print. All optional except the identity pair. */
export interface DigiTraxLabelData {
  /** Field 2 — one of the approved label files. */
  labelName?: DigiTraxLabelName
  /** Field 3 — installed printer driver name on the print server. */
  printer?: string
  /** Field 4 — Donation ID Number. */
  din: string
  /** Field 5 — ISBT product code, e.g. "S1155". */
  productCode: string
  /** Field 6 — see selection options. */
  bloodType?: string
  /** Field 7 — see selection options. */
  specialMessage?: string
  /** Field 8 — intended use statement. */
  intendedUse?: string
  /** Field 9 — see selection options. */
  donationType?: string
  /** Field 10. */
  donorType?: string
  /** Field 11 — do not send when a GRID number is used. */
  donorName?: string
  /** Field 12 — do not send when a GRID number is used. */
  donorNumber?: string
  /** Field 13 — ISO date (YYYY-MM-DD); serialized as "DD MMM YYYY". */
  donorDob?: string
  /** Field 14. */
  recipientName?: string
  /** Field 15. */
  recipientId?: string
  /** Field 16 — ISO date; serialized as "DD MMM YYYY". */
  recipientDob?: string
  /** Fields 17/18/37 — ISO datetime; date, 24h time and cYYjjjHHmm barcode. */
  collectionAt?: string
  /** Fields 20/21/22 — ISO datetime; blank when `expirationText` is used. */
  expirationAt?: string
  /** Field 24 — uppercase division character, e.g. "A". */
  division1?: string
  /** Field 25 — lowercase division character, e.g. "a". */
  division2?: string
  /** Fields 26–28. */
  collectionFacility?: [string?, string?, string?]
  /** Fields 29–31. */
  processingFacility?: [string?, string?, string?]
  /** Field 32 — mL. */
  productVolume?: number
  /** Field 33 — mL. */
  anticoagulantVolume?: number
  /** Field 34. */
  heparinConcentration?: number
  /** Field 35. */
  anticoagulant2Volume?: number
  /** Field 36 — anticoagulant name. */
  anticoagulant?: string
  /** Field 38 — when used, donor name/number/DOB must be blank. */
  gridNumber?: string
  /** Field 39 — replaces the expiration date/time/barcode entirely. */
  expirationText?: string
  /** Field 40 — e.g. "Investigational Drug". Not usable together with SEC. */
  productCodeType?: string
  /** Field 41. */
  secId?: string
  /** Field 42. */
  secTe?: string
  /** Field 43 — sponsor protocol identifier. */
  protocol?: string
  /** Field 44 — chain-of-identity identifier. */
  coiIdentifier?: string
  /** Fields 45–48 — manufacturer comment lines. */
  mfgText?: [string?, string?, string?, string?]
  /** Field 50 — "Clinical" prints "For Clinical Use Only". */
  clinicalCommercial?: string
}

export const DIGITRAX_LABEL_NAMES = [
  'i4x4Label.lbl',
  'i3.75x1.5Label.lbl',
  'i2x3Label.lbl',
  'i2x2Label.lbl',
  'i1.5x.75Label.lbl',
  'iCBSet.lbl',
  'iManufacturing.lbl',
] as const

export type DigiTraxLabelName = (typeof DIGITRAX_LABEL_NAMES)[number]

const ESC = '\x1b'
const EOT = '\x04'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Parse an ISO date or datetime as a wall-clock value. `new Date('YYYY-MM-DD')`
 * would read it as UTC midnight and print the previous day west of Greenwich,
 * so date-only strings are split by hand.
 */
function parseWallClock(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(iso)
  if (!m) return null
  const [, y, mo, d, h, min] = m
  const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h ?? '0'), Number(min ?? '0'))
  return Number.isNaN(date.getTime()) ? null : date
}

/** "DD MMM YYYY", the HemaTraxCT display date format. Empty for unparseable input. */
export function formatDigiTraxDate(iso: string | undefined): string {
  if (!iso) return ''
  const date = parseWallClock(iso)
  if (!date) return ''
  const dd = String(date.getDate()).padStart(2, '0')
  return `${dd} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`
}

/** 24-hour "HH:mm". Empty when the input has no time component. */
export function formatDigiTraxTime(iso: string | undefined): string {
  if (!iso) return ''
  const m = /[T ](\d{2}):(\d{2})/.exec(iso)
  return m ? `${m[1]}:${m[2]}` : ''
}

/**
 * The cYYjjjHHmm barcode payload (fields 22 and 37): century flag, 2-digit
 * year, Julian day-of-year, 24h time. The spec's own example is
 * `0201611100` = 09 Jun 2020 11:00 (c=0 for 20xx, day 161).
 */
export function digiTraxDateBarcode(iso: string | undefined): string {
  if (!iso) return ''
  const date = parseWallClock(iso)
  if (!date) return ''
  const year = date.getFullYear()
  const century = year >= 2000 && year < 2100 ? '0' : '1'
  const yy = String(year % 100).padStart(2, '0')
  const startOfYear = new Date(year, 0, 1)
  const julian = Math.floor((date.getTime() - startOfYear.getTime()) / 86_400_000) + 1
  const hhmm = formatDigiTraxTime(iso).replace(':', '') || '0000'
  return `${century}${yy}${String(julian).padStart(3, '0')}${hhmm}`
}

function num(v: number | undefined): string {
  return v == null || Number.isNaN(v) ? '' : String(v)
}

/**
 * Serialize to the HemaTraxCT text message: ESC, fields 2–50 in spec order
 * (one per line, blank when unknown), EOT. Field numbers are the spec's.
 */
export function buildDigiTraxMessage(data: DigiTraxLabelData): string {
  const expirationBlanked = !!data.expirationText
  const fields: string[] = [
    data.labelName ?? 'i4x4Label.lbl', // 2
    data.printer ?? '', // 3
    data.din, // 4
    data.productCode, // 5
    data.bloodType ?? '', // 6
    data.specialMessage ?? '', // 7
    data.intendedUse ?? '', // 8
    data.donationType ?? '', // 9
    data.donorType ?? '', // 10
    data.gridNumber ? '' : data.donorName ?? '', // 11 — blank when GRID is used
    data.gridNumber ? '' : data.donorNumber ?? '', // 12
    data.gridNumber ? '' : formatDigiTraxDate(data.donorDob), // 13
    data.recipientName ?? '', // 14
    data.recipientId ?? '', // 15
    formatDigiTraxDate(data.recipientDob), // 16
    formatDigiTraxDate(data.collectionAt), // 17
    formatDigiTraxTime(data.collectionAt), // 18
    '', // 19 — Collection Date UTC: we track wall-clock only
    expirationBlanked ? '' : formatDigiTraxDate(data.expirationAt), // 20
    expirationBlanked ? '' : formatDigiTraxTime(data.expirationAt), // 21
    expirationBlanked ? '' : digiTraxDateBarcode(data.expirationAt), // 22
    '', // 23 — Expiration Date UTC
    data.division1 ?? '0', // 24 — spec: "0 if not selected"
    data.division2 ?? '0', // 25
    data.collectionFacility?.[0] ?? '', // 26
    data.collectionFacility?.[1] ?? '', // 27
    data.collectionFacility?.[2] ?? '', // 28
    data.processingFacility?.[0] ?? '', // 29
    data.processingFacility?.[1] ?? '', // 30
    data.processingFacility?.[2] ?? '', // 31
    num(data.productVolume), // 32
    num(data.anticoagulantVolume), // 33
    num(data.heparinConcentration), // 34
    num(data.anticoagulant2Volume), // 35
    data.anticoagulant ?? '', // 36
    digiTraxDateBarcode(data.collectionAt), // 37
    data.gridNumber ?? '', // 38
    data.expirationText ?? '', // 39
    data.productCodeType ?? '', // 40
    data.secId ?? '', // 41
    data.secTe ?? '', // 42
    data.protocol ?? '', // 43
    data.coiIdentifier ?? '', // 44
    data.mfgText?.[0] ?? '', // 45
    data.mfgText?.[1] ?? '', // 46
    data.mfgText?.[2] ?? '', // 47
    data.mfgText?.[3] ?? '', // 48
    '', // 49 — Sponsor Logo: not enabled in this version
    data.clinicalCommercial ?? '', // 50
  ]
  return `${ESC}\r\n${fields.join('\r\n')}\r\n${EOT}`
}

// ─── Label Printing dialog vocabulary ────────────────────────────────────────

/** The three label types offered by the Label Printing popup (spec Image 1). */
export const PRODUCT_LABEL_TYPES = ['Standard', 'Clinical', 'Special'] as const
export type ProductLabelType = (typeof PRODUCT_LABEL_TYPES)[number]

/**
 * The four "Select Message" dropdowns, mapped onto the four HemaTraxCT fields
 * whose values are constrained to selection options (6–9). The option lists
 * are PLACEHOLDERS until Digi-Trax supplies the real selection tables —
 * TODO(digitrax): replace with the vendor lists (or make them configurable).
 */
export const SELECT_MESSAGE_FIELDS: {
  key: 'bloodType' | 'specialMessage' | 'intendedUse' | 'donationType'
  label: string
  options: string[]
}[] = [
  {
    key: 'bloodType',
    label: 'Blood type',
    options: ['Not Specified', 'A', 'B', 'AB', 'O', 'A Pos', 'A Neg', 'B Pos', 'B Neg', 'AB Pos', 'AB Neg', 'O Pos', 'O Neg'],
  },
  {
    key: 'specialMessage',
    label: 'Special message',
    options: ['Not Specified', 'For Autologous Use Only', 'For Allogeneic Use Only', 'Irradiated', 'Do Not Irradiate', 'Biohazard'],
  },
  {
    key: 'intendedUse',
    label: 'Intended use',
    options: ['Intended Use Not Specified', 'For Autologous Use Only', 'For Directed Donor Use Only', 'For Clinical Trial Use Only'],
  },
  {
    key: 'donationType',
    label: 'Donation type',
    options: ['Not Specified', 'Autologous', 'Allogeneic', 'Directed'],
  },
]

/** What the Label Printing dialog collects on top of the product itself. */
export interface ProductLabelSelections {
  labelType: ProductLabelType
  bloodType?: string
  specialMessage?: string
  intendedUse?: string
  donationType?: string
  /** The four free-text lines → MFG Text 1–4. */
  freeText: [string?, string?, string?, string?]
}

/** Assemble the label data for one created product plus the dialog selections. */
export function productLabelData(
  product: {
    din: string
    productDescriptionCode: string
    patientMrn?: string | null
    patientName?: string | null
    patientDob?: string | null
    createdAt: string
  },
  selections: ProductLabelSelections,
): DigiTraxLabelData {
  return {
    din: product.din,
    productCode: product.productDescriptionCode,
    bloodType: selections.bloodType,
    specialMessage: selections.specialMessage,
    intendedUse: selections.intendedUse,
    donationType: selections.donationType,
    recipientName: product.patientName ?? undefined,
    recipientId: product.patientMrn ?? undefined,
    recipientDob: product.patientDob ?? undefined,
    collectionAt: product.createdAt,
    mfgText: selections.freeText,
    // "Clinical" is the only value HemaTrax reacts to (prints "For Clinical
    // Use Only"); Standard and Special affect our own label styling only.
    clinicalCommercial: selections.labelType === 'Clinical' ? 'Clinical' : '',
  }
}

/**
 * Hand the built message to DigiTrax.
 *
 * TODO(digitrax): no endpoint exists yet. When Digi-Trax provides one this
 * becomes the only place to wire it (do NOT send from components). Until
 * then the message is logged so the print flow can be inspected end-to-end.
 */
export async function sendToDigiTrax(message: string): Promise<{ sent: boolean }> {
  console.info('[digitrax] transport not configured; built message:', JSON.stringify(message))
  return { sent: false }
}
